import type { Db } from "@corpuscomplete/core/drizzle";
import { z } from "zod/v4";
import { foodServing } from "../food/food.sql";
import { eq, desc, sql } from "drizzle-orm";
import {
  type LanguageModelResponseMetadata,
  type LanguageModelUsage,
  NoObjectGeneratedError,
  Output,
  generateObject,
  generateText,
  stepCountIs,
  tool,
} from "ai";
import { must } from "@corpuscomplete/core/utils";
import { Food } from "../food";
import { FoodSearch } from "../food-search";
import { Nutrient } from "../nutrient";
import type { UserActor } from "../../actor";
import type { ModelCallSettings } from "../../ai/model-call-settings";

/**
 * Food-matching boundary for candidate retrieval, classification, and disambiguation.
 *
 * Invariants:
 * - Match classification applies deterministic threshold/gap rules.
 * - Ambiguous resolution is explicit and may return no match.
 *
 * @see `packages/core/docs/namespace.md`
 */
export namespace FoodMatcher {
  export const descriptorSchema = z.object({
    name: z
      .string()
      .min(1)
      .describe("The food name in common usage form, e.g. 'Eggs', 'Chicken breast', 'Spinach'"),
    modifiers: z.array(z.string()).describe("Preparation terms like 'diced', 'raw', 'frozen'."),
    brand: z.string().nullable().describe("Brand name if present, e.g. 'Hellmann's'"),
  });
  export type Descriptor = z.infer<typeof descriptorSchema>;

  export const foodCandidateSnapshotSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    modifiers: z.array(z.string()),
    similarity: z.number(),
  });
  export type FoodCandidateSnapshot = z.infer<typeof foodCandidateSnapshotSchema>;

  export type FoodCandidate = Food.Info & { similarity: number };

  export interface FoodMatchQuery extends Descriptor {
    foodType: "generic" | "branded" | "recipe";
    collectionId?: string;
  }

  export type FoodMatchResult =
    | { type: "match"; food: FoodCandidate }
    | { type: "ambiguous"; candidates: FoodCandidate[] }
    | { type: "none" };

  export interface Config {
    candidateLimit?: number;
    perfectionThreshold?: number;
    relativeGapMultiplier?: number;
    minimumThreshold?: number;
    allowLooseSearchFallback?: boolean;
  }

  const DEFAULT_CONFIG: Required<Config> = {
    candidateLimit: 10,
    perfectionThreshold: 0.95,
    relativeGapMultiplier: 2,
    minimumThreshold: 0,
    allowLooseSearchFallback: false,
  };

  export async function findMatch(
    query: FoodMatchQuery,
    db: Db,
    actor: UserActor,
    config?: Config,
  ): Promise<FoodMatchResult> {
    const { candidateLimit, perfectionThreshold, relativeGapMultiplier, minimumThreshold } = {
      ...DEFAULT_CONFIG,
      ...config,
    };

    if (!query.name.trim()) {
      return { type: "none" };
    }

    const searchText =
      query.modifiers && query.modifiers.length > 0
        ? `${query.name}, ${query.modifiers.join(", ")}`
        : query.name;

    let searchResults = await FoodSearch.search(db, {
      text: searchText,
      actor,
      foodTypes: [query.foodType],
      collectionId: query.collectionId,
      limit: candidateLimit,
    });

    if (searchResults.length === 0 && config?.allowLooseSearchFallback) {
      searchResults = await FoodSearch.search(db, {
        text: searchText,
        actor,
        foodTypes: [query.foodType],
        collectionId: query.collectionId,
        limit: candidateLimit,
        useWordSimilarityGate: false,
      });
    }

    const candidates: FoodCandidate[] = searchResults.map(
      ({
        similarityName: _similarityName,
        normalizedBaseName: _normalizedBaseName,
        modifierText: _modifierText,
        ...food
      }) => food,
    );

    return classifyCandidates(candidates, {
      perfectionThreshold,
      relativeGapMultiplier,
      minimumThreshold,
    });
  }

  export function classifyCandidates(
    candidates: FoodCandidate[],
    config?: Pick<Config, "perfectionThreshold" | "relativeGapMultiplier" | "minimumThreshold">,
  ): FoodMatchResult {
    const perfectionThreshold = config?.perfectionThreshold ?? DEFAULT_CONFIG.perfectionThreshold;
    const relativeGapMultiplier =
      config?.relativeGapMultiplier ?? DEFAULT_CONFIG.relativeGapMultiplier;
    const minimumThreshold = config?.minimumThreshold ?? DEFAULT_CONFIG.minimumThreshold;

    const filtered = candidates.filter((c) => c.similarity >= minimumThreshold);

    if (filtered.length === 0) {
      return { type: "none" };
    }

    const maxSimilarity = must(filtered[0]).similarity;

    const perfectMatches = filtered.filter((c) => c.similarity >= perfectionThreshold);
    if (perfectMatches.length === 1) {
      return { type: "match", food: must(perfectMatches[0]) };
    }
    if (perfectMatches.length > 1) {
      return { type: "ambiguous", candidates: perfectMatches };
    }

    const competitive = filtered.filter(
      (c) => c.similarity >= maxSimilarity / relativeGapMultiplier,
    );

    if (competitive.length === 1) {
      return { type: "match", food: must(competitive[0]) };
    }

    if (competitive.length > 1) {
      return { type: "ambiguous", candidates: competitive };
    }

    return { type: "none" };
  }

  const disambiguationSchema = z.object({
    results: z.array(
      z.object({
        queryIndex: z.number(),
        hasMatch: z.boolean(),
        matchedCandidateIndex: z.number().nullable(),
        reasoning: z.string(),
      }),
    ),
  });

  export async function resolveAmbiguous(
    queries: Array<{ query: FoodMatchQuery; candidates: FoodCandidate[] }>,
    settings: ModelCallSettings,
  ): Promise<Array<FoodCandidate | null>> {
    if (queries.length === 0) {
      return [];
    }

    const { object } = await generateObject({
      ...settings,
      schema: disambiguationSchema,
      prompt: buildDisambiguationPrompt(queries),
    });

    return object.results.map((result, i) => {
      if (!result.hasMatch || result.matchedCandidateIndex === null) {
        return null;
      }
      return queries[i]?.candidates[result.matchedCandidateIndex] ?? null;
    });
  }

  function buildDisambiguationPrompt(
    queries: Array<{ query: FoodMatchQuery; candidates: FoodCandidate[] }>,
  ): string {
    const queryDescriptions = queries
      .map((q, qi) => {
        const modifiersStr =
          q.query.modifiers && q.query.modifiers.length > 0
            ? ` (${q.query.modifiers.join(", ")})`
            : "";
        const candidateLines = q.candidates
          .map((c, ci) => `  ${ci}. "${c.name}" (${(c.similarity * 100).toFixed()}% similar)`)
          .join("\n");
        return `Query ${qi}: "${q.query.name}"${modifiersStr}\nCandidates:\n${candidateLines}`;
      })
      .join("\n\n");

    return `For each ingredient query, determine if any candidate is a match.

Rules:
- Same food in different states (raw/cooked) = match
- Different foods that sound similar = no match
- If unsure, prefer no match

${queryDescriptions}`;
  }

  export interface BatchMatchResult {
    matched: Array<{ query: FoodMatchQuery; food: FoodCandidate }>;
    ambiguous: Array<{ query: FoodMatchQuery; candidates: FoodCandidate[] }>;
    gaps: FoodMatchQuery[];
  }

  export async function findMatchesBatch(
    queries: FoodMatchQuery[],
    db: Db,
    actor: UserActor,
    config?: Config & { chunkSize?: number },
  ): Promise<BatchMatchResult> {
    const chunkSize = config?.chunkSize ?? 100;
    const results: Array<{ query: FoodMatchQuery; result: FoodMatchResult }> = [];

    for (let i = 0; i < queries.length; i += chunkSize) {
      const chunk = queries.slice(i, i + chunkSize);
      const chunkResults = await Promise.all(
        chunk.map(async (query) => ({
          query,
          result: await findMatch(query, db, actor, config),
        })),
      );
      results.push(...chunkResults);
    }

    const matched: BatchMatchResult["matched"] = [];
    const ambiguous: BatchMatchResult["ambiguous"] = [];
    const gaps: BatchMatchResult["gaps"] = [];

    for (const { query, result } of results) {
      if (result.type === "match") {
        matched.push({ query, food: result.food });
      } else if (result.type === "ambiguous") {
        ambiguous.push({ query, candidates: result.candidates });
      } else {
        gaps.push(query);
      }
    }

    return { matched, ambiguous, gaps };
  }
}

/**
 * Item-level matcher boundary that pairs ingredient descriptors with food/serving candidates.
 *
 * Invariants:
 * - Item matches preserve food visibility constraints from upstream Food queries.
 * - Result shapes distinguish exact match, food-only, and ambiguous outcomes.
 *
 * @see `packages/core/docs/namespace.md`
 */
export namespace FoodItemMatcher {
  export const ItemDescriptor = FoodMatcher.descriptorSchema.extend({
    quantity: z
      .number()
      .nonnegative()
      .describe("The numeric quantity, e.g. 1, 2.5, 0.75. Null for 'to taste'."),
    unit: z
      .string()
      .nullable()
      .describe("The serving unit, e.g. 'cup', 'oz', 'large'. Null for unitless items."),
  });
  export type ItemDescriptor = z.infer<typeof ItemDescriptor>;

  export type ServingCandidate = Food.Serving & { similarity: number };
  export const servingCandidateSnapshotSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    modifiers: z.array(z.string()),
    similarity: z.number(),
    nutrients: Nutrient.amountsSchema.optional(),
  });
  export type ServingCandidateSnapshot = z.infer<typeof servingCandidateSnapshotSchema>;

  export type FoodCandidateWithServings = FoodMatcher.FoodCandidate & {
    servings: ServingCandidate[];
  };
  export const foodCandidateWithServingsSnapshotSchema =
    FoodMatcher.foodCandidateSnapshotSchema.extend({
      servings: z.array(servingCandidateSnapshotSchema),
    });
  export type FoodCandidateWithServingsSnapshot = z.infer<
    typeof foodCandidateWithServingsSnapshotSchema
  >;

  export const foodItemMatchResultSnapshotSchema = z.union([
    z.object({
      type: z.literal("foodAndServing"),
      food: FoodMatcher.foodCandidateSnapshotSchema,
      serving: servingCandidateSnapshotSchema,
    }),
    z.object({
      type: z.literal("foodOnly"),
      food: FoodMatcher.foodCandidateSnapshotSchema,
      servings: z.array(servingCandidateSnapshotSchema),
    }),
    z.object({
      type: z.literal("ambiguous"),
      foods: z.array(foodCandidateWithServingsSnapshotSchema),
    }),
  ]);
  export type FoodItemMatchResultSnapshot = z.infer<
    typeof foodItemMatchResultSnapshotSchema
  > | null;

  export type FoodItemMatchResult =
    | {
        type: "foodAndServing";
        food: FoodMatcher.FoodCandidate;
        serving: ServingCandidate;
      }
    | {
        type: "foodOnly";
        food: FoodMatcher.FoodCandidate;
        servings: ServingCandidate[];
      }
    | {
        type: "ambiguous";
        foods: FoodCandidateWithServings[];
      }
    | null;

  interface ServingMatchResult {
    type: "match" | "none" | "ambiguous";
    serving?: ServingCandidate;
    servings?: ServingCandidate[];
  }

  export async function matchItem(
    descriptor: ItemDescriptor,
    db: Db,
    actor: UserActor,
    config?: FoodMatcher.Config,
  ): Promise<FoodItemMatchResult> {
    const foodModifiersFromDescriptor = descriptor.modifiers.filter(Food.isModifier);

    const foodQuery: FoodMatcher.FoodMatchQuery = {
      name: descriptor.name,
      modifiers: foodModifiersFromDescriptor,
      brand: descriptor.brand,
      foodType: "generic",
    };

    const foodResult = await FoodMatcher.findMatch(foodQuery, db, actor, config);

    if (foodResult.type === "none") {
      return null;
    }

    if (foodResult.type === "match") {
      const servings = await getServingCandidates(foodResult.food.id, descriptor, db);
      const servingResult = classifyServingCandidates(servings);

      if (servingResult.type === "match" && servingResult.serving) {
        return {
          type: "foodAndServing",
          food: foodResult.food,
          serving: servingResult.serving,
        };
      }
      return {
        type: "foodOnly",
        food: foodResult.food,
        servings,
      };
    }

    const foodsWithServings = await Promise.all(
      foodResult.candidates.map(async (foodCandidate) => {
        const servings = await getServingCandidates(foodCandidate.id, descriptor, db);
        return { ...foodCandidate, servings };
      }),
    );

    return { type: "ambiguous", foods: foodsWithServings };
  }

  const SERVING_LIMIT = 3;
  const SERVING_PERFECTION_THRESHOLD = 0.95;

  async function getServingCandidates(
    foodId: string,
    descriptor: ItemDescriptor,
    db: Db,
  ): Promise<ServingCandidate[]> {
    const servingModifiersFromDescriptor = descriptor.modifiers.filter(Food.isServingModifier);

    if (!descriptor.unit) {
      const rows = await db
        .select({
          id: foodServing.id,
          foodId: foodServing.foodId,
          foodVersion: foodServing.foodVersion,
          sortIndex: foodServing.sortIndex,
          name: foodServing.name,
          modifiers: foodServing.modifiers,
          defaultAmount: foodServing.defaultAmount,
          massQuantity: foodServing.massQuantity,
          massUnitKey: foodServing.massUnitKey,
          volumeQuantity: foodServing.volumeQuantity,
          volumeUnitKey: foodServing.volumeUnitKey,
          nutrients: foodServing.nutrients,
        })
        .from(foodServing)
        .where(eq(foodServing.foodId, foodId))
        .execute();

      return rows.map((r) => ({ ...r, similarity: 1 }));
    }

    const searchName =
      servingModifiersFromDescriptor.length > 0
        ? `${descriptor.unit}, ${servingModifiersFromDescriptor.join(", ")}`
        : descriptor.unit;

    const sqlServingName = sql`${foodServing.name} || COALESCE(', ' || array_to_string(${foodServing.modifiers}, ', '), '')`;
    const similarity = sql<number>`similarity(${sqlServingName}, ${searchName})`;

    return db
      .select({
        id: foodServing.id,
        foodId: foodServing.foodId,
        foodVersion: foodServing.foodVersion,
        sortIndex: foodServing.sortIndex,
        name: foodServing.name,
        modifiers: foodServing.modifiers,
        defaultAmount: foodServing.defaultAmount,
        massQuantity: foodServing.massQuantity,
        massUnitKey: foodServing.massUnitKey,
        volumeQuantity: foodServing.volumeQuantity,
        volumeUnitKey: foodServing.volumeUnitKey,
        similarity,
        nutrients: foodServing.nutrients,
      })
      .from(foodServing)
      .where(eq(foodServing.foodId, foodId))
      .orderBy(desc(similarity))
      .limit(SERVING_LIMIT)
      .execute();
  }

  function classifyServingCandidates(candidates: ServingCandidate[]): ServingMatchResult {
    if (candidates.length === 0) {
      return { type: "none" };
    }

    const perfectMatches = candidates.filter((c) => c.similarity >= SERVING_PERFECTION_THRESHOLD);
    if (perfectMatches.length === 1) {
      return { type: "match", serving: must(perfectMatches[0]) };
    }

    if (perfectMatches.length > 1) {
      return { type: "ambiguous", servings: perfectMatches };
    }

    return { type: "ambiguous", servings: candidates };
  }

  export function formatDescriptor(descriptor: ItemDescriptor): string {
    const parts: string[] = [];
    if (descriptor.quantity !== null) {
      parts.push(String(descriptor.quantity));
    }
    if (descriptor.unit) {
      parts.push(descriptor.unit);
    }
    parts.push(descriptor.name);
    if (descriptor.modifiers.length > 0) {
      parts.push(`(${descriptor.modifiers.join(", ")})`);
    }
    if (descriptor.brand) {
      parts.push(`[brand: ${descriptor.brand}]`);
    }
    return parts.join(" ");
  }

  export function toFoodItemMatchResultSnapshot(
    result: FoodItemMatchResult,
  ): FoodItemMatchResultSnapshot {
    if (!result) {
      return null;
    }

    if (result.type === "foodAndServing") {
      return {
        type: "foodAndServing",
        food: toFoodCandidateSnapshot(result.food),
        serving: toServingCandidateSnapshot(result.serving),
      };
    }

    if (result.type === "foodOnly") {
      return {
        type: "foodOnly",
        food: toFoodCandidateSnapshot(result.food),
        servings: result.servings.map(toServingCandidateSnapshot),
      };
    }

    return {
      type: "ambiguous",
      foods: result.foods.map((food) => ({
        ...toFoodCandidateSnapshot(food),
        servings: food.servings.map(toServingCandidateSnapshot),
      })),
    };
  }

  function toFoodCandidateSnapshot(
    food: FoodMatcher.FoodCandidate,
  ): FoodMatcher.FoodCandidateSnapshot {
    return {
      id: food.id,
      name: food.name,
      modifiers: food.modifiers,
      similarity: food.similarity,
    };
  }

  function toServingCandidateSnapshot(serving: ServingCandidate): ServingCandidateSnapshot {
    return {
      id: serving.id,
      name: serving.name,
      modifiers: serving.modifiers,
      similarity: serving.similarity,
      nutrients: serving.nutrients,
    };
  }
}

export namespace FoodItemResolver {
  export interface ItemDisambiguationInput {
    context: string;
    items: Array<{
      descriptor: FoodItemMatcher.ItemDescriptor;
      matchResult: FoodItemMatcher.FoodItemMatchResult;
    }>;
  }

  export interface ItemDisambiguationResult {
    itemIndex: number;
    hasMatch: boolean;
    food: FoodMatcher.FoodCandidate | null;
    serving: FoodItemMatcher.ServingCandidate | null;
    suggestedQuantity: number | null;
  }

  const itemDisambiguationSchema = z.object({
    results: z.array(
      z.object({
        itemIndex: z.number(),
        hasMatch: z.boolean(),
        foodIndex: z.number().nullable(),
        servingIndex: z.number().nullable(),
        suggestedQuantity: z.number().nullable(),
        reasoning: z.string(),
      }),
    ),
  });

  export const resolveItemsOutputSchema = z.array(
    z.discriminatedUnion("outcome", [
      z.object({
        itemIndex: z.number().int().nonnegative(),
        outcome: z.literal("match"),
        foodServingId: z.string(),
        amount: z.number().positive(),
      }),
      z.object({
        itemIndex: z.number().int().nonnegative(),
        outcome: z.literal("no_match"),
      }),
    ]),
  );
  export type ResolveItemsOutput = z.infer<typeof resolveItemsOutputSchema>;

  const resolveItemsResponseSchema = z.object({
    results: resolveItemsOutputSchema,
  });

  const resolveItemsToolInputSchema = z.object({
    descriptors: z.array(FoodItemMatcher.ItemDescriptor),
  });

  const resolveItemsToolOutputSchema = z.object({
    results: z.array(FoodItemMatcher.foodItemMatchResultSnapshotSchema.nullable()),
  });

  export async function resolveAmbiguousItems(
    input: ItemDisambiguationInput,
    settings: ModelCallSettings,
  ): Promise<ItemDisambiguationResult[]> {
    const itemsToResolve = input.items.filter(
      (item) =>
        item.matchResult !== null &&
        (item.matchResult.type === "ambiguous" || item.matchResult.type === "foodOnly"),
    );

    if (itemsToResolve.length === 0) {
      return [];
    }

    const { object } = await generateObject({
      ...settings,
      schema: itemDisambiguationSchema,
      prompt: buildItemDisambiguationPrompt(input),
    });

    return object.results.map((result) => {
      const item = input.items[result.itemIndex];
      if (!item || !result.hasMatch || !item.matchResult) {
        return {
          itemIndex: result.itemIndex,
          hasMatch: false,
          food: null,
          serving: null,
          suggestedQuantity: null,
        };
      }

      const matchResult = item.matchResult;

      if (matchResult.type === "foodOnly") {
        const serving =
          result.servingIndex !== null ? (matchResult.servings[result.servingIndex] ?? null) : null;
        return {
          itemIndex: result.itemIndex,
          hasMatch: true,
          food: matchResult.food,
          serving,
          suggestedQuantity: result.suggestedQuantity,
        };
      }

      if (matchResult.type === "ambiguous") {
        const foodWithServings =
          result.foodIndex !== null ? (matchResult.foods[result.foodIndex] ?? null) : null;
        const serving =
          foodWithServings && result.servingIndex !== null
            ? (foodWithServings.servings[result.servingIndex] ?? null)
            : null;
        return {
          itemIndex: result.itemIndex,
          hasMatch: true,
          food: foodWithServings,
          serving,
          suggestedQuantity: result.suggestedQuantity,
        };
      }

      return {
        itemIndex: result.itemIndex,
        hasMatch: false,
        food: null,
        serving: null,
        suggestedQuantity: null,
      };
    });
  }

  export async function resolveUnmatchedItems(args: {
    meal: {
      slotName: string;
      name: string;
      ingredients: Array<FoodItemMatcher.ItemDescriptor & { id: string }>;
    };
    unresolvedItems: Array<{ descriptor: FoodItemMatcher.ItemDescriptor & { id: string } }>;
    db: Db;
    actor: UserActor;
    settings: ModelCallSettings;
    abortSignal?: AbortSignal;
  }): Promise<{
    results: ResolveItemsOutput;
    tokenUsage: {
      data: LanguageModelUsage;
      responseMetadata: LanguageModelResponseMetadata;
    } | null;
  }> {
    if (args.unresolvedItems.length === 0) {
      return { results: [], tokenUsage: null };
    }

    let parsedOutput: ResolveItemsOutput;
    let tokenUsage: {
      data: LanguageModelUsage;
      responseMetadata: LanguageModelResponseMetadata;
    } | null;

    try {
      const result = await generateText({
        ...args.settings,
        abortSignal: args.abortSignal,
        tools: {
          resolveItems: tool({
            description:
              "Try nutrition-database matches for transient descriptor phrasings. Input and output stay correlated by array order.",
            inputSchema: resolveItemsToolInputSchema,
            outputSchema: resolveItemsToolOutputSchema,
            execute: async (input) => ({
              results: await Promise.all(
                input.descriptors.map(async (descriptor: FoodItemMatcher.ItemDescriptor) =>
                  FoodItemMatcher.toFoodItemMatchResultSnapshot(
                    await FoodItemMatcher.matchItem(descriptor, args.db, args.actor, {
                      allowLooseSearchFallback: true,
                    }),
                  ),
                ),
              ),
            }),
          }),
        },
        output: Output.object({
          schema: resolveItemsResponseSchema,
          name: "ResolveItemsResults",
        }),
        stopWhen: stepCountIs(6),
        prompt: buildResolveItemsPrompt(args.meal, args.unresolvedItems),
      });

      parsedOutput = result.output.results;
      tokenUsage = {
        data: result.totalUsage,
        responseMetadata: {
          id: result.response.id,
          timestamp: result.response.timestamp,
          modelId: result.response.modelId,
          headers: result.response.headers,
        },
      };
    } catch (error) {
      if (!NoObjectGeneratedError.isInstance(error)) {
        throw error;
      }

      parsedOutput = [];
      tokenUsage =
        error.usage && error.response
          ? {
              data: error.usage,
              responseMetadata: error.response,
            }
          : null;
    }

    return { results: parsedOutput, tokenUsage };
  }

  function buildItemDisambiguationPrompt(input: ItemDisambiguationInput): string {
    const itemDescriptions = input.items
      .map((item, itemIndex) => {
        if (!item.matchResult) return null;
        if (item.matchResult.type === "foodAndServing") return null;

        const { descriptor, matchResult } = item;
        const descriptorStr = FoodItemMatcher.formatDescriptor(descriptor);

        if (matchResult.type === "foodOnly") {
          const servingLines = matchResult.servings
            .map((s, si) => {
              const modifiersStr = s.modifiers.length > 0 ? ` (${s.modifiers.join(", ")})` : "";
              return `    ${si}. "${s.name}"${modifiersStr} - ${(s.similarity * 100).toFixed()}% similar`;
            })
            .join("\n");
          return `Item ${itemIndex}: ${descriptorStr}
  Food: "${matchResult.food.name}" (${(matchResult.food.similarity * 100).toFixed()}% similar)
  Available servings:
${servingLines}`;
        }

        if (matchResult.type === "ambiguous") {
          const foodLines = matchResult.foods
            .map((f, fi) => {
              const servingLines = f.servings
                .map((s, si) => {
                  const modifiersStr = s.modifiers.length > 0 ? ` (${s.modifiers.join(", ")})` : "";
                  return `      ${si}. "${s.name}"${modifiersStr} - ${(s.similarity * 100).toFixed()}% similar`;
                })
                .join("\n");
              return `    ${fi}. "${f.name}" (${(f.similarity * 100).toFixed()}% similar)
    Servings:
${servingLines}`;
            })
            .join("\n");
          return `Item ${itemIndex}: ${descriptorStr}
  Food candidates:
${foodLines}`;
        }

        return null;
      })
      .filter((desc): desc is string => desc !== null)
      .join("\n\n");

    return `You are a registered dietitian helping to match recipe/meal ingredients to a nutrition database.

Context: ${input.context}

For each item, select the best matching food and serving combination. Use indices to identify your selections.

Rules:
- Same food in different states (raw/cooked) = acceptable match
- Different foods that sound similar = no match
- Prefer a different serving size over no match (adjust suggestedQuantity if needed)
- If the serving unit doesn't match well, suggest an adjusted quantity
- If unsure, prefer no match

${itemDescriptions}

For each item, respond with:
- itemIndex: the item number
- hasMatch: true if a match was found
- foodIndex: index of the selected food (null if foodOnly type or no match)
- servingIndex: index of the selected serving (null if no match)
- suggestedQuantity: adjusted quantity if the serving differs from requested (null to keep original)
- reasoning: brief explanation`;
  }

  function buildResolveItemsPrompt(
    meal: {
      slotName: string;
      name: string;
      ingredients: Array<FoodItemMatcher.ItemDescriptor & { id: string }>;
    },
    unresolvedItems: Array<{ descriptor: FoodItemMatcher.ItemDescriptor & { id: string } }>,
  ): string {
    const fullMealContext = meal.ingredients
      .map((ingredient, index) => `- ${index}. ${FoodItemMatcher.formatDescriptor(ingredient)}`)
      .join("\n");
    const unresolvedContext = unresolvedItems
      .map(
        ({ descriptor }, index) =>
          `Item ${index}: ${FoodItemMatcher.formatDescriptor(descriptor)}\nOriginal descriptor id: ${descriptor.id}`,
      )
      .join("\n\n");

    return `You are resolving leftover meal ingredient descriptors against a nutrition database.

Meal slot: ${meal.slotName}
Meal name: ${meal.name}

Full meal context:
${fullMealContext}

Unresolved items that still need final decisions:
${unresolvedContext}

You may call the resolveItems tool zero or more times.
- The tool input accepts only transient descriptor phrasings.
- Tool input/output arrays are correlated by array order only.
- You may probe alternate phrasings, including multiple phrasings for the same unresolved item.
- Do not mutate or reference persisted descriptor ids in tool calls.

Rules:
- outcome must be either match or no_match.
- Use match only when you can name a foodServingId returned by the tool.
- amount must be the final quantity for that serving.
- If you are unsure, return no_match.
- Respond with JSON only. Return an array of result objects.
`;
  }
}
